import type { Grade, CompanyIntel, Company } from '@/types';

// Escape HTML entities
export function escapeHtml(text: string | null | undefined): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

// Copy text to clipboard
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

// Format date
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// Calculate days since date
export function daysSince(dateString: string): number {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return -1;
  return Math.floor((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
}

// Get grade color class
export function getGradeColorClass(grade: Grade | null): string {
  switch (grade) {
    case 'A':
      return 'bg-green-500 text-white';
    case 'B':
      return 'bg-lime-500 text-white';
    case 'C':
      return 'bg-yellow-500 text-black';
    case 'D':
      return 'bg-orange-500 text-white';
    case 'F':
      return 'bg-red-500 text-white';
    default:
      return 'bg-gray-600 text-gray-400';
  }
}

// Calculate overall grade from intel data
export function calculateOverallGrade(intel: CompanyIntel | undefined): Grade | null {
  if (!intel) return null;

  const scores: number[] = [];

  // Glassdoor rating
  const glassdoorRating = intel.glassdoor?.data?.overview?.[0]?.rating;
  if (glassdoorRating) {
    const rating = parseFloat(String(glassdoorRating));
    if (rating >= 4.5) scores.push(5);
    else if (rating >= 4.0) scores.push(4);
    else if (rating >= 3.5) scores.push(3);
    else if (rating >= 3.0) scores.push(2);
    else scores.push(1);
  }

  // Consumer Intel scores
  const ci = intel.consumer_intel?.data;
  if (ci?.trustpilot?.[0]?.score) {
    const score = parseFloat(String(ci.trustpilot[0].score));
    if (score >= 4.5) scores.push(5);
    else if (score >= 4.0) scores.push(4);
    else if (score >= 3.5) scores.push(3);
    else if (score >= 3.0) scores.push(2);
    else scores.push(1);
  }

  if (ci?.google?.[0]?.rating) {
    const rating = parseFloat(String(ci.google[0].rating));
    if (rating >= 4.5) scores.push(5);
    else if (rating >= 4.0) scores.push(4);
    else if (rating >= 3.5) scores.push(3);
    else if (rating >= 3.0) scores.push(2);
    else scores.push(1);
  }

  // Pay transparency bonus
  const jobs = intel.linkedin_360?.data?.jobs || [];
  if (jobs.some((j) => j.salary)) {
    scores.push(4);
  }

  if (scores.length === 0) return null;

  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  if (avg >= 4.5) return 'A';
  if (avg >= 3.5) return 'B';
  if (avg >= 2.5) return 'C';
  if (avg >= 1.5) return 'D';
  return 'F';
}

// Calculate job recency grade
export function calculateJobRecencyGrade(intel: CompanyIntel | undefined): Grade | null {
  const jobs = intel?.linkedin_360?.data?.jobs || [];
  if (jobs.length === 0) return null;

  const recentJobs = jobs.filter((j) => {
    const posted = j.postedDate || j.publishedAt || j.postedAt;
    if (!posted) return false;
    const days = daysSince(posted);
    return days >= 0 && days <= 7;
  });

  const pct = (recentJobs.length / jobs.length) * 100;
  if (pct >= 80) return 'A';
  if (pct >= 60) return 'B';
  if (pct >= 40) return 'C';
  if (pct >= 20) return 'D';
  return 'F';
}

// Get target contacts from intel
export function getTargetContacts(
  _company: Company,
  intel: CompanyIntel | undefined
): Array<{ name: string; title: string }> {
  const targets: Array<{ name: string; title: string }> = [];

  // From LinkedIn 360 company data
  const employees = (intel?.linkedin_360?.data?.company as unknown as Array<{
    employees?: Array<{ name: string; title: string }>;
  }>)?.[0]?.employees || [];
  
  employees.slice(0, 5).forEach((e) => {
    if (
      e.title &&
      (e.title.includes('VP') ||
        e.title.includes('Director') ||
        e.title.includes('Head') ||
        e.title.includes('Manager') ||
        e.title.includes('Chief'))
    ) {
      targets.push({ name: e.name, title: e.title });
    }
  });

  // From Crunchbase
  const leadership = intel?.crunchbase?.data?.leadership || [];
  leadership.slice(0, 3).forEach((l) => {
    if (!targets.find((t) => t.name === l.name)) {
      targets.push({ name: l.name, title: l.title });
    }
  });

  return targets.slice(0, 5);
}

// Categorize job title into department
export function getDepartment(title: string): string {
  if (!title) return 'Other';
  const t = title.toLowerCase();

  if (
    t.includes('engineer') ||
    t.includes('developer') ||
    t.includes('software') ||
    t.includes('swe') ||
    t.includes('devops') ||
    t.includes('architect')
  )
    return 'Engineering';
  if (
    t.includes('data') ||
    t.includes('analyst') ||
    t.includes('scientist') ||
    t.includes('analytics') ||
    t.includes('bi ')
  )
    return 'Data & Analytics';
  if (
    t.includes('product') ||
    t.includes('pm ') ||
    t.includes('program manager')
  )
    return 'Product';
  if (
    t.includes('design') ||
    t.includes('ux') ||
    t.includes('ui') ||
    t.includes('creative')
  )
    return 'Design';
  if (
    t.includes('sales') ||
    t.includes('account exec') ||
    t.includes('business develop') ||
    t.includes('revenue')
  )
    return 'Sales';
  if (
    t.includes('marketing') ||
    t.includes('brand') ||
    t.includes('content') ||
    t.includes('seo') ||
    t.includes('growth')
  )
    return 'Marketing';
  if (
    t.includes('hr') ||
    t.includes('recruit') ||
    t.includes('people') ||
    t.includes('talent') ||
    t.includes('human resource')
  )
    return 'HR & Recruiting';
  if (
    t.includes('finance') ||
    t.includes('account') ||
    t.includes('financial') ||
    t.includes('controller') ||
    t.includes('treasury')
  )
    return 'Finance';
  if (
    t.includes('ops') ||
    t.includes('operations') ||
    t.includes('logistics') ||
    t.includes('supply chain')
  )
    return 'Operations';
  if (
    t.includes('legal') ||
    t.includes('counsel') ||
    t.includes('compliance')
  )
    return 'Legal';
  if (
    t.includes('executive') ||
    t.includes('chief') ||
    t.includes('vp ') ||
    t.includes('vice president') ||
    t.includes('director') ||
    t.includes('manager')
  )
    return 'Leadership';
  if (t.includes('intern') || t.includes('internship')) return 'Internships';
  if (
    t.includes('support') ||
    t.includes('customer') ||
    t.includes('service') ||
    t.includes('success')
  )
    return 'Customer Success';

  return 'Other';
}

// Check if text is English (basic heuristic)
export function isEnglish(text: string | null | undefined): boolean {
  if (!text || typeof text !== 'string') return true;
  const nonEnglish = /[àâäéèêëïîôùûüçæœÀÂÄÉÈÊËÏÎÔÙÛÜÇ]/;
  if (nonEnglish.test(text)) return false;
  const asciiLetters = text.match(/[a-zA-Z]/g) || [];
  const allLetters = text.match(/\p{L}/gu) || [];
  return allLetters.length === 0 || asciiLetters.length / allLetters.length > 0.8;
}

// Get string value safely from various formats
export function getStr(val: unknown): string {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string') return val;
  if (typeof val === 'number') return String(val);
  if (typeof val === 'object') {
    const obj = val as Record<string, unknown>;
    if (obj.text) return String(obj.text);
    if (obj.value) return String(obj.value);
    if (obj.name) return String(obj.name);
    return '';
  }
  return String(val);
}
